import {
  CONFIG,
  type Direction,
  EMOTE_BY_KEY,
  EMOTES,
  exhaustionPct,
  inExitZone,
  inZone,
  isEmoteId,
  type MapData,
  nearestDesk,
  parseMap,
  type TiledMap,
} from "@agency/shared";
import Phaser from "phaser";
import { net } from "../../net/NetClient.js";
import { type PlayerSnapshot, store } from "../../store/GameStore.js";
import { DeskView } from "../entities/DeskView.js";
import { LocalPlayer } from "../entities/LocalPlayer.js";
import { RemotePlayer } from "../entities/RemotePlayer.js";
import { Effects } from "../systems/Effects.js";
import { InteractionPrompt } from "../systems/InteractionPrompt.js";
import { OfficeLighting } from "../systems/OfficeLighting.js";

const DIR_DOWN = 0;
const DIR_LEFT = 1;
const DIR_RIGHT = 2;
const DIR_UP = 3;
const CEILING_FADE_MS = 250;

/** Facing for an input vector; with no input, keep the current facing. */
function directionFrom(dx: number, dy: number, current: Direction): Direction {
  if (Math.abs(dx) >= Math.abs(dy)) {
    if (dx < 0) return DIR_LEFT;
    if (dx > 0) return DIR_RIGHT;
  } else {
    if (dy < 0) return DIR_UP;
    if (dy > 0) return DIR_DOWN;
  }
  return current;
}

export class OfficeScene extends Phaser.Scene {
  private map!: MapData;
  private local?: LocalPlayer;
  private localDir: Direction = DIR_DOWN;
  private localSessionId = "";
  private readonly remotes = new Map<string, RemotePlayer>();
  private readonly desks = new Map<string, DeskView>();
  private prompt!: InteractionPrompt;
  private effects!: Effects;
  private lighting?: OfficeLighting;
  private unsubscribers: Array<() => void> = [];
  private lastCodePuff = 0;
  private lastExitInside = false;
  private lastRadioInside = false;
  private lastRadioNote = 0;
  /** Smoothed proximity gain; undefined until the first frame places the player. */
  private radioGain?: number;
  /** A move-to-cancel stopActivity is in flight for the current activity. */
  private stopRequested = false;
  private bathroomCeiling?: Phaser.Tilemaps.TilemapLayer | Phaser.Tilemaps.TilemapGPULayer;
  private bathroomFade?: Phaser.Tweens.Tween;
  private insideBathroom = false;

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private keys!: Record<string, Phaser.Input.Keyboard.Key>;
  private emoteKeys: Phaser.Input.Keyboard.Key[] = [];

  constructor() {
    super("Office");
  }

  create(): void {
    const raw = this.cache.json.get("officeRaw") as TiledMap;
    this.map = parseMap(raw);
    this.buildTilemap();
    this.lighting = new OfficeLighting(this, this.map, this.bathroomCeiling);

    for (const desk of this.map.desks) this.desks.set(desk.id, new DeskView(this, desk));

    this.localSessionId = store.sessionId;
    this.prompt = new InteractionPrompt(this);
    this.effects = new Effects(this);
    this.setupInput();
    this.subscribeStore();

    for (const [sessionId, snap] of store.players) this.ensureAvatar(sessionId, snap);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private buildTilemap(): void {
    const map = this.make.tilemap({ key: "office" });
    const tileset = map.addTilesetImage("office", "tiles", 16, 16, 0, 0);
    if (!tileset) return;
    map.createLayer("floor", tileset, 0, 0)?.setDepth(0);
    map.createLayer("walls", tileset, 0, 0)?.setDepth(1);
    map.createLayer("furniture", tileset, 0, 0)?.setDepth(2);
    map.createLayer("above", tileset, 0, 0)?.setDepth(100_000);
    if (this.map.layers.includes("bathroom_ceiling")) {
      // Cover avatars and their overhead labels while the room is concealed.
      this.bathroomCeiling = map
        .createLayer("bathroom_ceiling", tileset, 0, 0)
        ?.setDepth(100_000 + map.heightInPixels + 1)
        .setVisible(true)
        .setAlpha(1);
    }

    const cam = this.cameras.main;
    cam.setBounds(0, 0, map.widthInPixels, map.heightInPixels);
    cam.setZoom(this.scale.width < 900 ? 2 : 3);
    cam.setBackgroundColor("#12141c");
  }

  private setupInput(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    this.cursors = kb.createCursorKeys();
    this.keys = kb.addKeys("W,A,S,D,E,ESC,SPACE") as Record<string, Phaser.Input.Keyboard.Key>;
    const emoteNames = ["ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX"];
    this.emoteKeys = emoteNames.map((name) =>
      kb.addKey(
        Phaser.Input.Keyboard.KeyCodes[name as keyof typeof Phaser.Input.Keyboard.KeyCodes],
      ),
    );
  }

  private subscribeStore(): void {
    this.unsubscribers.push(
      store.on("playerAdd", ({ sessionId, player }) => this.ensureAvatar(sessionId, player)),
      store.on("playerRemove", ({ sessionId }) => this.removeAvatar(sessionId)),
      store.on("correction", ({ x, y }) => this.local?.snapTo(x, y)),
      store.on("emote", ({ sessionId, emote }) => {
        const view = this.viewFor(sessionId);
        const glyph = isEmoteId(emote) ? EMOTES[emote] : "✨";
        view?.showEmote(glyph, this.time.now + 3000);
      }),
      store.on("chat", (msg) => {
        if (msg.kind !== "player" || !msg.sessionId) return;
        this.viewFor(msg.sessionId)?.showBubble(
          msg.text,
          this.time.now + CONFIG.BUBBLE_DURATION_MS,
        );
      }),
      store.on("results", () => {
        if (this.local) this.effects.burst(this.local.pos.x, this.local.pos.y - 10);
      }),
    );
  }

  private ensureAvatar(sessionId: string, snap: PlayerSnapshot): void {
    if (this.localSessionId && sessionId === this.localSessionId) {
      if (!this.local) {
        this.local = new LocalPlayer(this, snap.avatarId, snap.nickname, {
          x: snap.x,
          y: snap.y,
        });
        this.localDir = snap.dir as Direction;
        this.cameras.main.startFollow(this.local.view.container, true, 0.12, 0.12);
      }
      return;
    }
    if (this.remotes.has(sessionId)) return;
    const remote = new RemotePlayer(this, snap.avatarId, snap.nickname, snap.x, snap.y);
    this.remotes.set(sessionId, remote);
  }

  private removeAvatar(sessionId: string): void {
    const remote = this.remotes.get(sessionId);
    if (remote) {
      remote.destroy();
      this.remotes.delete(sessionId);
    }
  }

  private viewFor(sessionId: string) {
    if (sessionId === this.localSessionId) return this.local?.view;
    return this.remotes.get(sessionId)?.view;
  }

  override update(time: number, delta: number): void {
    const dtMs = Math.min(delta, 100);
    const localSnap = store.players.get(this.localSessionId);

    if (this.local && localSnap) this.updateLocal(time, dtMs, localSnap);
    this.updateBathroomCeiling();
    this.lighting?.update();

    for (const [sessionId, remote] of this.remotes) {
      const snap = store.players.get(sessionId);
      if (!snap) continue;
      remote.push(time, snap.x, snap.y, snap.dir as Direction, snap.moving);
      remote.update(time, snap.activity, snap.connected);
      remote.view.setExhaustion(exhaustionPct(snap.exhaustion));
      remote.view.setName(snap.nickname);
    }

    // Expire emotes and chat bubbles.
    this.local?.view.update(this.time.now);
    for (const remote of this.remotes.values()) remote.view.update(this.time.now);

    for (const [deskId, deskView] of this.desks) {
      deskView.setOccupied((store.desks.get(deskId) ?? "") !== "");
    }

    this.updatePrompt(localSnap);
    this.updateRadio(time, dtMs);
    this.updateEffects(time);
  }

  private updateLocal(time: number, dtMs: number, snap: PlayerSnapshot): void {
    if (!this.local) return;
    const kb = this.input.keyboard;
    const enabled = kb?.enabled ?? true;

    let dx = 0;
    let dy = 0;
    if (enabled) {
      if (this.cursors.left.isDown || this.keys.A?.isDown) dx -= 1;
      if (this.cursors.right.isDown || this.keys.D?.isDown) dx += 1;
      if (this.cursors.up.isDown || this.keys.W?.isDown) dy -= 1;
      if (this.cursors.down.isDown || this.keys.S?.isDown) dy += 1;
    }

    const moving = dx !== 0 || dy !== 0;
    const dir = directionFrom(dx, dy, this.localDir);
    this.localDir = dir;
    const canMove = snap.activity === "idle";
    // Movement cancels an activity: ask the server to stop once, then move
    // when it reports idle (it ignores positions from busy players).
    if (canMove) this.stopRequested = false;
    else {
      // Busy players are pinned server-side (e.g. seated at a desk); local
      // prediction may have drifted before the activity patch arrived.
      if (this.local.pos.x !== snap.x || this.local.pos.y !== snap.y) {
        this.local.snapTo(snap.x, snap.y);
      }
      if (moving && !this.stopRequested) {
        this.stopRequested = true;
        net.send("stopActivity");
      }
    }

    if (moving && canMove) {
      this.local.move(this.map, { x: dx, y: dy }, CONFIG.PLAYER_SPEED_PX_S, dtMs);
      this.local.view.setDirection(dir, true);
    } else {
      this.local.view.setDirection(dir, false);
    }

    this.local.view.setActivity(snap.activity);
    this.local.view.setName(snap.nickname);
    this.local.view.setExhaustion(exhaustionPct(snap.exhaustion));
    this.local.report(dir, moving && canMove, time, snap.moving);

    if (!enabled) return;
    this.handleActionKeys(snap);
  }

  private updateBathroomCeiling(): void {
    const inside = !!(
      this.local &&
      this.map.bathroomArea &&
      inZone(this.local.pos, this.map.bathroomArea)
    );
    if (!this.bathroomCeiling || inside === this.insideBathroom) return;
    this.insideBathroom = inside;
    // Reverse from the current opacity if the player crosses back mid-fade.
    this.bathroomFade?.stop();
    this.bathroomFade = this.tweens.add({
      targets: this.bathroomCeiling,
      alpha: inside ? 0 : 1,
      duration: CEILING_FADE_MS,
      ease: "Sine.easeInOut",
    });
  }

  private handleActionKeys(snap: PlayerSnapshot): void {
    const interactKey = this.keys.E;
    const spaceKey = this.keys.SPACE;
    const escKey = this.keys.ESC;
    const interactPressed =
      (interactKey && Phaser.Input.Keyboard.JustDown(interactKey)) ||
      (spaceKey && Phaser.Input.Keyboard.JustDown(spaceKey));
    if (interactPressed) {
      const pos = this.local!.pos;
      if (inExitZone(pos, this.map.exitZone)) {
        net.send("exit");
      } else if (snap.activity !== "idle") {
        net.send("stopActivity");
      } else {
        net.send("interact", {});
      }
    }
    if (escKey && Phaser.Input.Keyboard.JustDown(escKey) && snap.activity !== "idle") {
      net.send("stopActivity");
    }
    this.emoteKeys.forEach((key, index) => {
      if (Phaser.Input.Keyboard.JustDown(key)) {
        const emote = EMOTE_BY_KEY[String(index + 1)];
        if (emote) net.send("emote", { emote });
      }
    });
  }

  private updateEffects(time: number): void {
    if (!this.local) return;
    if (time - this.lastCodePuff < 450) return;
    this.lastCodePuff = time;
    for (const snap of store.players.values()) {
      if (snap.activity !== "working") continue;
      const view = this.viewFor(snap.sessionId);
      if (view) this.effects.codePuff(view.container.x, view.container.y);
    }
    for (const machine of this.map.coffeeMachines) {
      const someoneDrinking = [...store.players.values()].some((p) => p.activity === "coffee");
      if (someoneDrinking && Math.random() < 0.4) this.effects.steam(machine.x, machine.y);
    }
  }

  /** Hi-fi panel trigger, proximity volume and the ♪ notes everyone sees. */
  private updateRadio(time: number, dtMs: number): void {
    const area = this.map.radioArea;
    const source = this.map.radioPos;
    if (!this.local || !area || !source) return;
    const pos = this.local.pos;

    const inside = inZone(pos, area);
    if (inside !== this.lastRadioInside) {
      this.lastRadioInside = inside;
      store.emit("radioArea", { inside });
    }

    const inRelax = this.map.relaxZones.some((zone) => inZone(pos, zone));
    const distance = Math.hypot(pos.x - source.x, pos.y - source.y);
    const target = inRelax
      ? 1
      : Math.max(CONFIG.RADIO_MIN_GAIN, 1 - distance / CONFIG.RADIO_FALLOFF_PX);
    // Ease toward the target so walking doesn't step the volume.
    let gain =
      this.radioGain === undefined
        ? target
        : this.radioGain + (target - this.radioGain) * Math.min(1, dtMs / 250);
    if (Math.abs(gain - target) < 0.005) gain = target;
    this.radioGain = gain;
    // Skip tiny steps, but always land exactly on the target.
    if (Math.abs(gain - store.radioGain) > 0.005 || (gain === target && gain !== store.radioGain)) {
      store.radioGain = gain;
      store.emit("radioGain", gain);
    }

    if (store.radio.playing && time - this.lastRadioNote > 600) {
      this.lastRadioNote = time;
      this.effects.note(source.x, source.y - 10);
    }
  }

  private updatePrompt(snap: PlayerSnapshot | undefined): void {
    if (!this.local) return;
    const pos = this.local.pos;
    let text = "";

    const insideExit = inExitZone(pos, this.map.exitZone);
    if (insideExit !== this.lastExitInside) {
      this.lastExitInside = insideExit;
      store.emit("exitZone", { inside: insideExit });
    }

    if (snap?.activity === "working") {
      text = "Working… [E]/[Esc] stop";
    } else if (snap?.activity === "coffee") {
      text = "Drinking… [Esc] stop";
    } else if (insideExit) {
      text = "[E] Leave the office";
    } else if (this.map.relaxZones.some((zone) => inZone(pos, zone))) {
      text = (snap?.exhaustion ?? 0) > 0 ? "[E] Grab a coffee" : "You're fully rested";
    } else {
      const desk = nearestDesk(pos, this.map.desks, CONFIG.INTERACT_RADIUS_PX);
      if (desk) {
        const occupant = store.desks.get(desk.id) ?? "";
        if (snap?.burnedOut) text = "Too exhausted — grab a coffee ☕";
        else if (store.phase === "celebrating") text = "Project shipped! Wait for the next one";
        else if (occupant && occupant !== this.localSessionId) text = "Desk taken";
        else text = "[E] Start working";
      }
    }

    // Sit on top of the avatar's label stack so it never covers name or chat.
    this.prompt.set(text, pos.x, this.local.view.labelTop() - 2);
  }

  private teardown(): void {
    this.lighting?.destroy();
    this.lighting = undefined;
    this.bathroomFade?.stop();
    this.bathroomFade = undefined;
    this.bathroomCeiling = undefined;
    this.insideBathroom = false;
    if (this.lastRadioInside) store.emit("radioArea", { inside: false });
    this.lastRadioInside = false;
    this.radioGain = undefined;
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    for (const remote of this.remotes.values()) remote.destroy();
    this.remotes.clear();
    this.local?.destroy();
    this.local = undefined;
  }
}
